import { RouteComponentProps, useHistory, useLocation, useParams } from 'react-router-dom';
import { parse } from '@tock/query-string';
import React, { useEffect, useState } from 'react';
import _ from 'lodash';

import { ACTION_JOIN_WAITLIST } from 'utils/analytics/eventTypeConstantsAmplitude';
import { ANY_EXPERIENCE_OPTION } from 'components/business/waitlist-dialog/WaitlistPreferencesForm';
import { DateTimeFormatter, LocalDate, ZoneId } from '@js-joda/core';
import { Dialog, DialogContent, DialogHeading } from 'omakase';
import { IWithContextProps, consumer, core, toJSON, withContext } from '@tock/coretypes';
import {
  WaitlistRequestFields,
  WaitlistRequestForm,
} from 'components/business/waitlist-dialog/WaitlistRequestForm';
import { WaitlistRequestStatus } from 'routes/business/search/ExperienceSearchRoute';
import { WaitlistSuccessPage } from 'components/business/waitlist-dialog/WaitlistSuccessPage';
import { adjustDateForCutOff } from 'utils/constants';
import { displayedTimesSelector } from 'selectors/displayedTimesSelectors';
import { offeringsSelector } from 'selectors/calendarSelectors';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import WaitlistDialog, { Fields, Page } from 'components/business/waitlist-dialog/WaitlistDialog';
import connect, { DefaultConnectProps, StoreType, useActions } from '@tock/shared/utils/connect';

export interface ExperienceOption {
  label: string;
  sizes: number[];
  services: SelectOption[];
  value: number | null;
}

interface MatchParams {
  widget?: string;
  businessDomain: string;
}

export interface WaitlistLocationState {
  status?: WaitlistRequestStatus;
  nearestAvailabilityMinutes?: number;
}

interface OwnProps extends RouteComponentProps<MatchParams, {}, WaitlistLocationState> {
  experienceId?: number;
  backUrl?: string;
}

interface ConnectProps extends DefaultConnectProps {
  displayedTimes: string[];
  calendar: StoreType['calendar'];
  offerings?: consumer.ConsumerOfferings;
}

type Props = OwnProps & ConnectProps & IWithContextProps;

export const WAITLIST_PAGES = {
  PREFERENCES_PAGE: 'PREFERENCES_PAGE',
  CONTACT_PAGE: 'CONTACT_PAGE',
  SUCCESS_PAGE: 'SUCCESS_PAGE',
} as const;

const WaitlistRoute: React.FC<Props> = (props) => {
  const {
    context,
    app,
    calendar: { calendar },
    offerings,
    experienceId,
    displayedTimes,
  } = props;
  const { actions } = useActions();
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const history = useHistory();
  const location = useLocation<{
    status?: WaitlistRequestStatus;
    nearestAvailabilityMinutes?: number;
  }>();
  const params = useParams<MatchParams>();
  const [page, setPage] = useState<Page>(WAITLIST_PAGES.PREFERENCES_PAGE);
  const [waitlistEntry, setWaitlistEntry] = useState<core.IWaitlist>();
  const hasFormikForm = useFeatureFlag('CONSUMER_WAITLIST_FORMIK');

  const handleClose = () => {
    if (BROWSER && window.RunningInWidget) {
      window.parent.postMessage({ source: 'tock', action: 'close' }, '*');
    } else if (props.backUrl) {
      history.replace(props.backUrl);
    } else {
      props.replaceRouteWith('/');
    }
  };

  const handleSubmit = async (values: WaitlistRequestFields) => {
    const {
      desiredDates,
      desiredStartTime,
      desiredEndTime,
      desiredExperience,
      desiredPartySize,
      ...patron
    } = values;

    const desiredDateTimeRange = desiredDates.map((date) =>
      formatStringToInstant(date.toString(), desiredStartTime, desiredEndTime)
    );

    const ticketTypeId =
      desiredExperience === ANY_EXPERIENCE_OPTION ? undefined : Number(desiredExperience);

    const requestData: core.IWaitlist = {
      desiredSize: Number(desiredPartySize),
      desiredDateTimeRange,
      ticketTypeId,
      ...patron,
    };

    try {
      const payload = await addToWaitlist(requestData);
      return payload;
    } catch {
      return undefined;
    }
  };

  const formatStringToInstant = (date: string, startTime: string, endTime: string) => {
    const startDateTime = adjustDateForCutOff(date, startTime);
    const endDateTime = adjustDateForCutOff(date, endTime);
    const zoneId = ZoneId.of(app.config?.business?.timeZone as string);

    return {
      startDateTime: startDateTime.atZone(zoneId).format(DateTimeFormatter.ISO_LOCAL_DATE_TIME),
      endDateTime: endDateTime.atZone(zoneId).format(DateTimeFormatter.ISO_LOCAL_DATE_TIME),
    };
  };

  const handleAddToWaitlist = (formValues: Fields) => {
    const { selectedDate, desiredStartTime, desiredEndTime } = formValues;
    const desiredDateTimeRange = selectedDate.map((date) =>
      formatStringToInstant(date, desiredStartTime, desiredEndTime)
    );

    const requestData: core.IWaitlist = {
      desiredDateTimeRange,
      desiredSize: formValues.partySize,
      email: formValues.email,
      firstName: formValues.firstName,
      isoCountryCode: formValues.isoCountryCode,
      lastName: formValues.lastName,
      phone: formValues.phone,
      phoneCountryCode: formValues.phoneCountryCode,
      ticketTypeId: formValues.experience,
      guestNote: formValues.guestNote,
    };

    addToWaitlist(requestData);
  };

  const addToWaitlist = async (request: core.IWaitlist) => {
    // @lframnes - blindly copied this guest logic when separating functions
    // no idea if this is needed what putting this data in redux does...
    const guest = {
      firstName: request.firstName,
      lastName: request.lastName,
      email: request.email,
      phone: request.phone,
    };

    actions.lastGuest.setLastGuest(guest);

    const { search, state } = location;
    const { fromPage } = parse(search);
    const waitlistStatus = state?.status;
    const waitlistNearestAvailabilityMinutes = state?.nearestAvailabilityMinutes;
    let waitlistSource: string;

    switch (fromPage) {
      case 'experience':
        waitlistSource = 'Experience-level search';
        break;
      case 'event_detail':
        waitlistSource = 'Event detail page';
        break;
      default:
        waitlistSource = 'Business-level search';
        break;
    }

    amplitudeSendEvent(ACTION_JOIN_WAITLIST, {
      waitlistSource,
      waitlistStatus,
      waitlistNearestAvailabilityMinutes,
    });

    request.phone = await maybeGetBeautifiedPhone(request);

    try {
      const { payload } = await actions.waitlist.addToWaitlist(request, false);

      //remove setWaitlistEntry and setPage when CONSUMER_WAITLIST_FORMIK is cleaned up
      setWaitlistEntry(toJSON<core.Waitlist>(payload));
      setPage(WAITLIST_PAGES.SUCCESS_PAGE);
      return toJSON<core.Waitlist>(payload);
    } catch {
      return;
    }
  };

  const maybeGetBeautifiedPhone = async ({ phone, phoneCountryCode }: core.IWaitlist) => {
    try {
      const { payload } = await actions.patron.validatePhoneRequest({
        phone: phone ?? '',
        phoneCountryCode: phoneCountryCode ?? '',
      });

      return payload.isValid ? payload.beautifiedPhone : phone;
    } catch {
      return phone;
    }
  };

  const { date: dateFromQuery, size: sizeFromProps, time } = parse(location.search);

  // A venue with no available party sizes has no bookable experiences. Rendering
  // the waitlist form in that state drives an empty-party-size update loop that
  // freezes the page, so notify the guest and close instead of showing the form.
  const availablePartySizes = _.uniq(
    _.flatMap(calendar?.ticketType ?? [], (ticketType) => ticketType.purchaseSize ?? [])
  );
  const hasNoBookableExperiences = !!calendar && _.isEmpty(availablePartySizes);

  useEffect(() => {
    if (hasNoBookableExperiences) {
      actions.toast.showError('Notify is not available.');
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNoBookableExperiences]);

  if (!offerings || !calendar || !app.config?.business) {
    return null;
  }

  if (hasNoBookableExperiences) {
    return null;
  }

  const size = sizeFromProps ? _.toInteger(sizeFromProps) : undefined;

  const waitlistDialog = (
    <WaitlistDialog
      displayedTimes={displayedTimes}
      business={app.config.business}
      date={dateFromQuery}
      openDates={calendar.openDate}
      detectedLocation={app.detectedLocation as core.DetectedLocation}
      experienceId={experienceId}
      isAuthenticated={!!app.jwtToken}
      isWidget={!_.isUndefined(params.widget)}
      location={location}
      offerings={offerings}
      onClose={handleClose}
      onSubmit={handleAddToWaitlist}
      patron={app.patron ?? undefined}
      showError={actions.toast.showError}
      size={size}
      ticketTypeCalendarDetails={
        calendar.ticketTypeCalendarDetails as consumer.TicketTypeCalendarDetails[]
      }
      ticketTypes={calendar.ticketType as core.TicketType[]}
      isFeatureEnabled={context.isFeatureEnabled}
      page={page}
      setPage={setPage}
    />
  );

  const initialRequest = hasFormikForm
    ? {
        desiredPartySize: sizeFromProps,
        desiredDates: dateFromQuery ? [LocalDate.parse(dateFromQuery)] : [],
        desiredExperience: experienceId?.toString(),
        desiredStartTime: time,
        firstName: app.patron?.firstName,
        lastName: app.patron?.lastName,
        email: app.patron?.email,
        phone: app.patron?.phone,
        phoneCountryCode: app.patron?.phoneCountryCode,
        isoCountryCodes: app.patron?.isoCountryCode,
      }
    : {};

  return (
    <Dialog open={true} onClose={handleClose}>
      {hasFormikForm ? (
        <WaitlistRequestForm
          onSubmit={handleSubmit}
          initialRequest={initialRequest}
          onCancel={handleClose}
        />
      ) : page === 'SUCCESS_PAGE' ? (
        <WaitlistSuccessPage
          waitlistEntry={waitlistEntry}
          onClose={handleClose}
          isAuthenticated={!!app.jwtToken}
        />
      ) : (
        <>
          <DialogHeading onClose={handleClose}>Notify</DialogHeading>
          <DialogContent disablePadding>{waitlistDialog}</DialogContent>
        </>
      )}
    </Dialog>
  );
};

const mapStateToProps = (state: StoreType) => {
  return {
    app: state.app,
    calendar: state.calendar,
    selection: state.selection,
    lock: state.lock,
    offerings: offeringsSelector(state),
    displayedTimes: displayedTimesSelector(state),
  };
};

export default connect<OwnProps, ConnectProps>(withContext(WaitlistRoute), mapStateToProps);
